import { useCallback, useEffect, useState, type ReactNode } from 'react'
// BarChart3 는 BIC 카드 2(LMPA Dashboard) 주석 해제 시 다시 import
import { LogOut, Shield, Users } from 'lucide-react'
import { Link, Route, Routes, useParams, useNavigate, useSearchParams } from 'react-router-dom'
import {
  fetchSession,
  loginWithGoogle,
  logout,
  fetchAccess,
  type AuthSession,
  type PortalAccess,
} from './auth'
import {
  APPS,
  BIC_APPS,
  LEFERIST_APPS,
  isAppGatedForUser,
  PORTAL_LABELS,
  creatorDashboardsCard,
  type AppEntry,
  type PortalTab,
} from './apps'
import { YoutubeDownloaderPage } from './YoutubeDownloader'
import { PdfCompressorPage } from './PdfCompressor'
import { SalesforceProductsPage } from './SalesforceProducts'
import { QrGeneratorPage } from './QrGenerator'
import { ConfirmationDocsPage } from './ConfirmationDocs'
import { TownhallPage } from './Townhall'
import PlatformPage from './platform/PlatformPage'
import CreatorSlotsPage from './creator-slots/CreatorSlotsPage'
import CreatorsPage from './creators/CreatorsPage'
import CreatorListPage from './creators/CreatorListPage'
import { BrandLinksPage } from './creators/BrandLinksPage'
import { CREATORS_ENABLED } from './creators/featureFlag'
import {
  AdminAiTokensPage,
  AdminArtifactPage,
  AdminCreatorsPage,
  AdminOAuthPage,
  AdminOverviewPage,
  AdminUsersPage,
  AdminYoutubePage,
} from './AdminDashboard'
import { fetchWhoAmI } from './adminApi'

type AuthState =
  | { status: 'loading' }
  | { status: 'signed-out' }
  | { status: 'signed-in'; session: AuthSession }
  | { status: 'error'; message: string }

export default function App() {
  const [auth, setAuth] = useState<AuthState>({ status: 'loading' })
  // Admin status — null while unknown, true if /api/admin/whoami says
  // so. Drives the conditional Admin link in the header. Probed only
  // after sign-in so anonymous traffic doesn't hit the admin surface
  // at all.
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null)

  useEffect(() => {
    fetchSession()
      .then((session) =>
        setAuth(session ? { status: 'signed-in', session } : { status: 'signed-out' }),
      )
      .catch((err: unknown) =>
        setAuth({ status: 'error', message: err instanceof Error ? err.message : String(err) }),
      )
  }, [])

  useEffect(() => {
    if (auth.status !== 'signed-in') {
      setIsAdmin(null)
      return
    }
    let cancelled = false
    fetchWhoAmI()
      .then((r) => {
        if (!cancelled) setIsAdmin(r.isAdmin)
      })
      .catch(() => {
        if (!cancelled) setIsAdmin(false)
      })
    return () => {
      cancelled = true
    }
  }, [auth.status])

  // Signed-out → the intro hero owns the full viewport (its own top rail
  // sits flush, no blue header bar above). Loading/error/signed-in keep
  // the standard chrome. Tools at /{path} also gate behind auth.
  if (auth.status === 'signed-out') {
    return (
      <div className="min-h-screen bg-surface-base">
        <SignInPanel />
      </div>
    )
  }

  // (머지 정리) 구 스캐폴드의 `/creators` 풀블리드 조기 return 제거 — 현 라우팅에선
  // /creators = 목록(CreatorListPage), 워크스페이스는 /creators/:creatorId (Routes 안).
  // 남겨두면 목록 진입 시 CreatorsPage가 id 없이 렌더 → /creators 리다이렉트 무한 루프.
  return (
    <div className="min-h-full bg-surface-base">
      <Header auth={auth} isAdmin={isAdmin === true} onLogout={() => void handleLogout(setAuth)} />
      <main className="mx-auto max-w-[1320px] px-s-6 pb-s-11">
        <Routes>
          <Route path="/" element={<Body auth={auth} />} />
          <Route
            path="/youtubedownloader"
            element={
              auth.status === 'signed-in' ? (
                <YoutubeDownloaderPage session={auth.session} />
              ) : (
                <Body auth={auth} />
              )
            }
          />
          <Route
            path="/pdfcompressor"
            element={
              auth.status === 'signed-in' ? (
                <PdfCompressorPage session={auth.session} />
              ) : (
                <Body auth={auth} />
              )
            }
          />
          <Route
            path="/salesforce-products"
            element={
              auth.status === 'signed-in' ? (
                <RequireEmployee>
                  <SalesforceProductsPage session={auth.session} />
                </RequireEmployee>
              ) : (
                <Body auth={auth} />
              )
            }
          />
          <Route
            path="/qr"
            element={
              auth.status === 'signed-in' ? (
                <QrGeneratorPage session={auth.session} />
              ) : (
                <Body auth={auth} />
              )
            }
          />
          <Route
            path="/confirmations"
            element={
              auth.status === 'signed-in' ? (
                <RequireEmployee>
                  <ConfirmationDocsPage session={auth.session} />
                </RequireEmployee>
              ) : (
                <Body auth={auth} />
              )
            }
          />
          <Route
            path="/townhall"
            element={
              auth.status === 'signed-in' ? (
                <RequireEmployee>
                  <TownhallPage />
                </RequireEmployee>
              ) : (
                <Body auth={auth} />
              )
            }
          />
          <Route path="/labels/:label/creators" element={<CreatorGridPage />} />
          <Route path="/platform" element={<PlatformPage />} />
          <Route path="/creator-slots" element={<CreatorSlotsPage />} />
          {/* Creators 워크스페이스 = BIC 카테고리. 피처 플래그(CREATORS_ENABLED)와 권한
              (RequireBic)은 다른 축이다 — 플래그는 "아직 안 만들었다", 권한은 "당신 몫이 아니다". */}
          {CREATORS_ENABLED && (
            <Route
              path="/creators"
              element={
                <RequireBic>
                  <CreatorListPage />
                </RequireBic>
              }
            />
          )}
          {CREATORS_ENABLED && (
            <Route
              path="/creators/brand-links"
              element={
                <RequireBic>
                  <BrandLinksPage />
                </RequireBic>
              }
            />
          )}
          {CREATORS_ENABLED && (
            <Route
              path="/creators/:creatorId"
              element={
                <RequireBic>
                  <CreatorsPage />
                </RequireBic>
              }
            />
          )}
          <Route path="/admin" element={<AdminOverviewPage />} />
          <Route path="/admin/creators" element={<AdminCreatorsPage />} />
          <Route path="/admin/users" element={<AdminUsersPage />} />
          <Route path="/admin/youtube" element={<AdminYoutubePage />} />
          <Route path="/admin/artifact" element={<AdminArtifactPage />} />
          <Route path="/admin/oauth" element={<AdminOAuthPage />} />
          <Route path="/admin/ai-tokens" element={<AdminAiTokensPage />} />
          {/* Any other path falls back to the home grid — the app is small
              and this is friendlier than a 404 for a typo'd path. */}
          <Route path="*" element={<Body auth={auth} />} />
        </Routes>
      </main>
      <SiteFooter />
    </div>
  )
}

/** Canonical footer shown across the whole platform. The About / Privacy
 *  links point to server-rendered pages (not SPA routes), so plain <a>
 *  full-navigation anchors are correct here. */
function SiteFooter() {
  return (
    <footer className="mt-s-11 border-t border-line-hairline">
      <div className="mx-auto flex max-w-[1320px] flex-wrap items-center justify-center gap-x-s-6 gap-y-s-2 px-s-6 py-s-5 lf-caption text-fg-3">
        <span>© Leferi Co., Ltd.</span>
        <a href="/about" className="hover:text-fg-1">
          About
        </a>
        <a href="/privacy" className="hover:text-fg-1">
          Privacy Policy
        </a>
        <a href="mailto:creator@leferi.com" className="hover:text-fg-1">
          Contact
        </a>
      </div>
    </footer>
  )
}

async function handleLogout(setAuth: (s: AuthState) => void) {
  await logout()
  setAuth({ status: 'signed-out' })
}

function Header({
  auth,
  isAdmin,
  onLogout,
}: {
  auth: AuthState
  isAdmin: boolean
  onLogout: () => void
}) {
  return (
    <header className="bg-brand-blue text-fg-inverse">
      <div className="mx-auto flex max-w-[1320px] items-center justify-between px-s-6 py-s-4">
        <Link to="/" className="lf-wordmark text-[18px] text-fg-inverse">
          Leferi Apps
        </Link>
        {auth.status === 'signed-in' ? (
          <div className="flex items-center gap-s-3">
            {isAdmin ? (
              <Link
                to="/admin"
                className="inline-flex items-center gap-s-2 rounded-r-1 border border-line-on-dark px-s-3 py-s-1 lf-body-sm text-fg-inverse transition-colors duration-base ease-standard hover:border-white/40 hover:bg-white/10 focus-visible:outline-none focus-visible:shadow-focus"
                aria-label="Admin dashboard"
              >
                <Shield size={13} strokeWidth={1.6} />
                <span>Admin</span>
              </Link>
            ) : null}
            <SignedInChip session={auth.session} onLogout={onLogout} />
          </div>
        ) : null}
      </div>
    </header>
  )
}

function SignedInChip({ session, onLogout }: { session: AuthSession; onLogout: () => void }) {
  return (
    <div className="flex items-center gap-s-3 lf-body-sm">
      <Avatar session={session} />
      <span className="text-fg-inverse">{session.name ?? session.email}</span>
      <button
        type="button"
        onClick={onLogout}
        aria-label="Sign out"
        title="Sign out"
        className="inline-flex h-7 w-7 items-center justify-center rounded-r-1 border border-line-on-dark text-fg-inverse transition-colors duration-base ease-standard hover:border-white/40 hover:bg-white/10"
      >
        <LogOut size={14} strokeWidth={1.5} />
      </button>
    </div>
  )
}

function Avatar({ session }: { session: AuthSession }) {
  const [broken, setBroken] = useState(false)
  if (session.picture && !broken) {
    return (
      <img
        src={session.picture}
        alt=""
        referrerPolicy="no-referrer"
        onError={() => setBroken(true)}
        className="h-7 w-7 rounded-r-pill bg-white/15 object-cover ring-1 ring-line-on-dark"
      />
    )
  }
  return (
    <span className="flex h-7 w-7 items-center justify-center rounded-r-pill bg-white/15 text-[11px] font-medium text-fg-inverse ring-1 ring-line-on-dark">
      {initialsOf(session)}
    </span>
  )
}

function initialsOf(session: AuthSession): string {
  const source = session.name?.trim() || session.email
  const letters = source
    .split(/\s+/)
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
  return (letters || source[0] || '?').toUpperCase()
}

function Body({ auth }: { auth: AuthState }) {
  if (auth.status === 'loading') {
    return <div className="py-s-11 lf-body text-fg-3">Loading.</div>
  }
  if (auth.status === 'error') {
    return (
      <div className="py-s-11">
        <p className="lf-eyebrow text-status-danger">▪ Connection</p>
        <p className="mt-s-3 lf-body text-fg-1">Could not reach the server.</p>
        <p className="mt-s-1 lf-body-sm text-fg-3">{auth.message}</p>
      </div>
    )
  }
  // signed-out is handled in App() so the intro hero owns the viewport;
  // this branch is unreachable, but the explicit narrow keeps TS happy.
  if (auth.status === 'signed-out') return null
  return <LandingView session={auth.session} />
}

/**
 * Variant D — sign-in intro hero.
 *
 * Per BX B.2: Pink and Blue never share a gradient, but they're allowed
 * to meet through TIME on the same surface. The headline period and the
 * Google CTA fill animate in opposite-phase on a 12s loop (see
 * .lf-swap-accent / .lf-swap-cta keyframes in @leferi/ui).
 *
 * No product names appear here on purpose — the page stays stable as the
 * app catalog grows.
 */
function SignInPanel() {
  return (
    <div className="lf-signin-hero relative mx-auto flex min-h-screen w-full max-w-[1320px] flex-col justify-between px-s-5 pb-s-7 pt-s-6 sm:px-s-10 sm:pb-s-9 sm:pt-s-8">
      {/* Top rail */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-s-2">
          <span aria-hidden className="block h-3 w-3 bg-brand-pink" />
          <span className="lf-wordmark text-[15px]">Leferi&nbsp;Apps</span>
        </div>
        <p className="lf-eyebrow">
          <span className="lf-num">apps.leferi.com</span>
        </p>
      </div>

      {/* Hero */}
      <div className="grid grid-cols-12 items-end gap-s-7">
        <div className="col-span-12 lg:col-span-8">
          <p className="lf-eyebrow mb-s-5 inline-flex items-center gap-s-2">
            <span aria-hidden>▪</span>
            <span>Internal</span>
            <span className="inline-block h-px w-4 bg-current/30 align-middle" />
            <span>for Leferist</span>
          </p>

          {/* Headline — period crossfades blue ↔ pink, opposite-phase
             to the CTA fill. lf-display-1 sized up per the handoff. */}
          <h1
            className="lf-display-1 leading-[0.92]"
            style={{ fontSize: 'clamp(56px, 11vw, 160px)' }}
          >
            Apps<span className="lf-swap-accent">.</span>
          </h1>

          <p
            className="mt-s-6 max-w-[22ch] lf-display-2"
            style={{
              fontSize: 'clamp(26px, 2.6vw, 36px)',
              fontWeight: 500,
              lineHeight: 1.15,
            }}
          >
            for the people who build&nbsp;Leferi.
          </p>
          <p className="mt-s-4 max-w-[44ch] lf-body">One workspace. Every Leferi tool.</p>
        </div>

        <div className="col-span-12 lg:col-span-4">
          <div className="max-w-[360px] lg:ml-auto">
            <SignInGoogleButton onClick={loginWithGoogle} />
            <p className="mt-s-4 lf-eyebrow flex items-center gap-s-2">
              <span aria-hidden>▪</span>
              <span>
                Restricted to <span className="lf-num">@leferi.com</span> Google accounts.
              </span>
            </p>
          </div>
        </div>
      </div>

      {/* Footer hairline */}
      <div className="flex items-center justify-between border-t border-line-hairline pt-s-4">
        <p className="lf-eyebrow text-ink-500">An evolving toolkit.</p>
        <p className="lf-eyebrow">© Leferi Co.,Ltd.</p>
      </div>
    </div>
  )
}

function SignInGoogleButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="group lf-swap-cta inline-flex w-full items-center justify-center gap-s-3 rounded-r-2 px-s-5 py-s-4 lf-h4 transition-colors duration-base ease-standard focus-visible:outline-none focus-visible:shadow-focus"
    >
      <span className="inline-flex h-7 w-7 items-center justify-center rounded-r-1 bg-paper">
        <GoogleGIcon />
      </span>
      <span>Sign in with Google</span>
      <span
        aria-hidden
        className="ml-s-1 transition-transform duration-base ease-standard group-hover:translate-x-[2px]"
      >
        →
      </span>
    </button>
  )
}

function GoogleGIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden>
      <path
        fill="#4285F4"
        d="M17.64 9.2c0-.64-.06-1.25-.17-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.71v2.26h2.91c1.7-1.57 2.69-3.88 2.69-6.61z"
      />
      <path
        fill="#34A853"
        d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.91-2.26c-.81.54-1.84.86-3.05.86-2.34 0-4.33-1.58-5.04-3.71H.96v2.33A8.997 8.997 0 0 0 9 18z"
      />
      <path
        fill="#FBBC05"
        d="M3.96 10.71A5.41 5.41 0 0 1 3.68 9c0-.59.1-1.17.28-1.71V4.96H.96A8.997 8.997 0 0 0 0 9c0 1.45.35 2.82.96 4.04l3-2.33z"
      />
      <path
        fill="#EA4335"
        d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58C13.46.89 11.42 0 9 0A8.997 8.997 0 0 0 .96 4.96l3 2.33C4.67 5.16 6.66 3.58 9 3.58z"
      />
    </svg>
  )
}

/**
 * BIC 전용 라우트 게이트 — 랜딩에서 탭을 숨기는 것과 **별개로** URL 직접 진입을 막는다.
 *
 * 탭만 숨기면 "안 보임"이지 "못 들어감"이 아니다. 링크를 받았거나 히스토리에 남은 사람은
 * 그대로 들어간다. 진짜 경계는 서버(bicGate)지만, 여기서 막지 않으면 화면은 열리고 API 만
 * 403 이라 빈 대시보드에 에러가 깔린 상태를 보게 된다.
 *
 * 로딩 중엔 아무것도 그리지 않는다 — 허용/거부 어느 쪽으로도 깜빡이지 않게. 조회 실패는
 * 거부로 본다(fail closed): 서버가 어차피 403 이므로 열어봐야 빈 화면이다.
 */
function RequireBic({ children }: { children: ReactNode }) {
  return (
    <RequireAccess
      allowed={(a) => a.bic}
      message="Brand Intelligence Consulting 접근 권한이 없습니다. 관리자에게 문의해 주세요."
    >
      {children}
    </RequireAccess>
  )
}

/** 전사 직원 전용 앱(apps.ts employeeOnly)의 라우트 게이트 — 랜딩 탭 노출과 같은 값을 본다.
 *  진짜 경계는 서버 employeeGate 다. */
function RequireEmployee({ children }: { children: ReactNode }) {
  return (
    <RequireAccess
      allowed={(a) => a.employee}
      message="전사 직원만 사용할 수 있습니다. 관리자에게 문의해 주세요."
    >
      {children}
    </RequireAccess>
  )
}

function RequireAccess({
  allowed,
  message,
  children,
}: {
  allowed: (access: PortalAccess) => boolean
  message: string
  children: ReactNode
}) {
  const [state, setState] = useState<'loading' | 'allowed' | 'denied'>('loading')
  useEffect(() => {
    let cancelled = false
    fetchAccess()
      .then((a) => {
        if (!cancelled) setState(allowed(a) ? 'allowed' : 'denied')
      })
      .catch(() => {
        if (!cancelled) setState('denied')
      })
    return () => {
      cancelled = true
    }
  }, [])
  if (state === 'loading') return null
  if (state === 'denied') {
    return (
      <div className="pt-s-9">
        <div className="border border-line-hairline bg-surface-soft p-s-7">
          <p className="lf-eyebrow text-status-danger">▪ No access</p>
          <p className="mt-s-3 lf-body text-fg-1">{message}</p>
          <Link to="/" className="mt-s-5 inline-block lf-caption text-brand-blue underline">
            홈으로
          </Link>
        </div>
      </div>
    )
  }
  return <>{children}</>
}

function LandingView({ session }: { session: AuthSession }) {
  const firstName = session.name?.split(/\s+/)[0] ?? session.email.split('@')[0] ?? 'there'

  // Per-user landing access. While loading, assume everyone-only so the
  // page never flashes a forbidden tab.
  const [access, setAccess] = useState<PortalAccess>({
    everyone: true,
    labels: [],
    bic: false,
    employee: false,
  })
  const [accessLoaded, setAccessLoaded] = useState(false)
  useEffect(() => {
    let cancelled = false
    fetchAccess()
      .then((a) => {
        if (!cancelled) setAccess(a)
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setAccessLoaded(true)
      })
    return () => {
      cancelled = true
    }
  }, [])

  const canEveryone = access.everyone
  const allowedLabels = PORTAL_LABELS.filter((l) => access.labels.includes(l.key))
  const canLabels = allowedLabels.length > 0
  // BIC 탭 — 전용 권한(portalAccess.bic)을 가진 사용자와 어드민만. everyone 과 **분리**돼 있다:
  // 워크스페이스가 크리에이터 사업 데이터를 담고 있어 전사 기본 공개 대상이 아니다.
  // 레이블 탭(canLabels = 부여된 레이블이 하나라도 있는가)과 같은 모양이다 — 노출 플래그
  // 없이 권한만 본다. CREATORS_ENABLED 는 "만들었는가"라 BIC_APPS 가 비면 탭도 뜨지 않는다.
  //
  // 화면 게이팅만으로는 URL 을 아는 사람을 못 막는다 — 서버의 bicGate 가 API 를 403 으로 막고,
  // 아래 라우트 게이트가 /creators 직접 진입을 막는다. 셋이 같은 값(access.bic)을 본다.
  const canBic = access.bic && BIC_APPS.length > 0
  // for Leferist 탭 — 전사 직원 전용 앱. BIC 와 달리 어드민도 바이패스하지 않는다(access.employee).
  const canLeferist = access.employee && LEFERIST_APPS.length > 0

  // Deep-link support: returning from a creator grid lands on
  // /?tab=labels&label=<key> so "back" is deterministic (not browser
  // history). Seed the tab + label sub-tab from the URL on mount.
  const [searchParams] = useSearchParams()
  const urlTab = searchParams.get('tab')
  const urlLabel = searchParams.get('label')

  // Active top tab — default to the first one the user can see.
  // Corrective switches only run AFTER access loads — otherwise the initial
  // default access ({everyone:true, labels:[]}) would bounce a deep-linked
  // ?tab=labels straight back to the everyone tab before labels arrive.
  const [tab, setTab] = useState<PortalTab>(
    urlTab === 'labels' || urlTab === 'bic' || urlTab === 'leferist' ? urlTab : 'everyone',
  )
  useEffect(() => {
    if (!accessLoaded) return
    if (tab === 'everyone' && !canEveryone) {
      if (canLabels) setTab('labels')
      else if (canLeferist) setTab('leferist')
    }
    if (tab === 'labels' && !canLabels && canEveryone) setTab('everyone')
    if (tab === 'bic' && !canBic) setTab(canEveryone ? 'everyone' : 'labels')
    if (tab === 'leferist' && !canLeferist) setTab(canEveryone ? 'everyone' : 'labels')
  }, [accessLoaded, tab, canEveryone, canLabels, canBic, canLeferist])

  // Active label sub-tab. Prefer the URL label when it's one the user can
  // access; fall back to the first allowed label. Never clobber the URL
  // label while access is still loading (allowedLabels temporarily empty).
  const [labelKey, setLabelKey] = useState<string | null>(urlLabel)
  useEffect(() => {
    if (allowedLabels.length === 0) return
    if (allowedLabels.some((l) => l.key === labelKey)) return
    const desired =
      urlLabel && allowedLabels.some((l) => l.key === urlLabel) ? urlLabel : allowedLabels[0].key
    setLabelKey(desired)
  }, [allowedLabels, labelKey, urlLabel])

  const showTabBar = canEveryone || canLabels || canBic || canLeferist
  const activeLabel = allowedLabels.find((l) => l.key === labelKey) ?? null

  return (
    <div className="pt-s-9">
      <p className="lf-eyebrow">▪ Apps</p>
      <h1 className="mt-s-3 lf-display-1">
        Hello, {firstName}
        <span className="text-brand-pink">.</span>
      </h1>
      <p className="mt-s-4 lf-body max-w-[640px]">
        Pick a tool. More will land here as the team needs them.
      </p>

      {/* Top tabs — only the tabs this user can access are shown. */}
      {showTabBar ? (
        <div className="mt-s-8 flex flex-wrap items-end gap-s-7 border-b border-line-hairline">
          {canEveryone ? (
            <TopTab active={tab === 'everyone'} onClick={() => setTab('everyone')}>
              for Everyone
            </TopTab>
          ) : null}
          {canLabels ? (
            <TopTab active={tab === 'labels'} onClick={() => setTab('labels')}>
              for Labels &amp; Creator Solutions
            </TopTab>
          ) : null}
          {canBic ? (
            <TopTab active={tab === 'bic'} onClick={() => setTab('bic')}>
              for Brand Intelligence Consulting
            </TopTab>
          ) : null}
          {canLeferist ? (
            <TopTab active={tab === 'leferist'} onClick={() => setTab('leferist')}>
              for Leferist
            </TopTab>
          ) : null}
        </div>
      ) : null}

      {!showTabBar ? (
        <div className="mt-s-9 border border-line-hairline bg-surface-soft p-s-7">
          <p className="lf-eyebrow text-status-danger">▪ No access</p>
          <p className="mt-s-3 lf-body text-fg-1">
            아직 접근 권한이 없습니다. 관리자에게 문의해 주세요.
          </p>
        </div>
      ) : tab === 'everyone' && canEveryone ? (
        <EveryoneTab session={session} />
      ) : tab === 'labels' && canLabels ? (
        <LabelsTab
          labels={allowedLabels}
          activeKey={labelKey}
          onSelect={setLabelKey}
          activeLabelName={activeLabel?.name ?? ''}
        />
      ) : tab === 'bic' && canBic ? (
        <BicTab />
      ) : tab === 'leferist' && canLeferist ? (
        <LeferistTab session={session} />
      ) : null}
    </div>
  )
}

function TopTab({
  active,
  onClick,
  children,
}: {
  active: boolean
  onClick: () => void
  children: ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-current={active ? 'page' : undefined}
      style={{ fontFamily: 'var(--font-display)' }}
      className={[
        'relative -mb-px border-b-2 pb-s-3 pt-s-1 lf-eyebrow transition-colors duration-base ease-standard focus-visible:outline-none focus-visible:shadow-focus',
        active ? 'border-brand-blue text-fg-1' : 'border-transparent text-fg-3 hover:text-fg-2',
      ].join(' ')}
    >
      {children}
    </button>
  )
}

function EveryoneTab({ session }: { session: AuthSession }) {
  const apps = APPS.filter((app) => !app.employeeOnly)
  const featured = apps.find((app) => app.featured)
  const rest = apps.filter((app) => !app.featured)
  return (
    <>
      {featured ? (
        <div className="mt-s-8">
          <FeaturedCard app={featured} index={apps.indexOf(featured)} />
        </div>
      ) : null}
      {rest.length > 0 ? (
        <div className="mt-s-6 grid grid-cols-1 gap-s-4 sm:grid-cols-2 lg:grid-cols-3">
          {rest.map((app) => (
            <AppCard
              key={app.id}
              app={app}
              index={apps.indexOf(app)}
              gated={isAppGatedForUser(app, session.email)}
            />
          ))}
        </div>
      ) : null}
    </>
  )
}

function LeferistTab({ session }: { session: AuthSession }) {
  return (
    <div className="mt-s-8 grid grid-cols-1 gap-s-4 sm:grid-cols-2 lg:grid-cols-3">
      {LEFERIST_APPS.map((app, i) => (
        <AppCard
          key={app.id}
          app={app}
          index={i}
          gated={isAppGatedForUser(app, session.email)}
        />
      ))}
    </div>
  )
}

// BIC 섹션 카드 그라디언트 — 시안(Leferi Apps.dc.html) 원값 그대로(시안=정답지, imagery 예외).
const BIC_CARD1_BG =
  'radial-gradient(60% 80% at 74% 40%, rgba(86,83,250,0.72) 0%, rgba(20,20,80,0) 60%), ' +
  'radial-gradient(50% 70% at 20% 82%, rgba(140,150,255,0.4) 0%, rgba(15,15,60,0) 60%), ' +
  'linear-gradient(135deg,#0e1240 0%,#2a2ba0 55%,#0a0c2c 100%), #0c1030'
// BIC 카드 2(LMPA Dashboard) 주석 해제 시 함께 복원
// const BIC_CARD2_BG =
//   'radial-gradient(60% 80% at 72% 42%, rgba(86,83,250,0.68) 0%, rgba(20,20,80,0) 60%), ' +
//   'radial-gradient(50% 70% at 22% 80%, rgba(120,125,251,0.45) 0%, rgba(15,15,60,0) 60%), ' +
//   'linear-gradient(135deg,#0c0f38 0%,#23239a 55%,#080a24 100%), #0a0e2a'

function BicTab() {
  return (
    <div className="mt-s-8">
      {/* 섹션 eyebrow */}
      <div className="mb-[14px] flex items-center gap-s-2">
        <span className="h-1.5 w-1.5 bg-brand-blue" aria-hidden />
        <span
          className="font-medium text-fg-3"
          style={{ fontFamily: 'var(--font-text)', fontSize: '11px', letterSpacing: '0.18em' }}
        >
          BRAND INTELLIGENCE CONSULTING
        </span>
      </div>

      {/* 2단 헤더 — 좌 디스플레이 헤딩 / 우 설명문 */}
      <div className="mb-10 grid gap-12 md:[grid-template-columns:1.15fr_1fr]">
        <h2
          className="font-bold text-ink-800"
          style={{
            fontFamily: 'var(--font-display)',
            fontSize: 'clamp(40px,4.4vw,60px)',
            lineHeight: 1.02,
            letterSpacing: '-0.01em',
          }}
        >
          Brand sales &amp;
          <br />
          ad operations<span className="text-brand-blue">.</span>
        </h2>
        <p
          className="self-end text-fg-2"
          style={{ fontFamily: 'var(--font-text)', fontSize: '16px', lineHeight: 1.6 }}
        >
          브랜드 세일즈와 광고 프로젝트 운영을 담당하는 부서를 위한 공간입니다. 브랜드 리서치,
          세일즈 파이프라인, 캠페인 운영에 필요한 전용 도구가 이곳에 하나씩 추가됩니다.
        </p>
      </div>

      {/* 카드 2개 — 1 Creators(Link) · 2 LMPA Dashboard(div, 시안상 링크 없음) */}
      <div className="grid gap-6 md:grid-cols-2">
        <Link
          to="/creators"
          className="group relative block h-80 overflow-hidden text-white focus-visible:shadow-focus focus-visible:outline-none"
          style={{ background: BIC_CARD1_BG }}
        >
          <BicCardChrome ordinal="01" />
          <BicCardBody
            icon={<Users className="h-[18px] w-[18px]" aria-hidden />}
            title="Creators"
            desc="Browse and match creators to brand campaigns."
          />
        </Link>
        {/* <div className="relative h-80 overflow-hidden text-white" style={{ background: BIC_CARD2_BG }}>
          <BicCardChrome ordinal="02" />
          <BicCardBody
            icon={<BarChart3 className="h-[18px] w-[18px]" aria-hidden />}
            title="LMPA Dashboard"
            desc="Track ad project performance across markets."
          />
        </div> */}
      </div>
    </div>
  )
}

function BicCardChrome({ ordinal }: { ordinal: string }) {
  return (
    <>
      <div className="absolute left-6 top-6 flex items-center gap-2">
        <span className="h-1.5 w-1.5 bg-white/70" aria-hidden />
        <span
          style={{
            fontFamily: 'var(--font-text)',
            fontSize: '11px',
            letterSpacing: '0.2em',
            color: 'rgba(255,255,255,0.72)',
          }}
        >
          APPS — {ordinal}
        </span>
      </div>
      <span
        className="absolute right-6 top-6 inline-flex items-center gap-1.5 bg-brand-blue px-[9px] py-[4px]"
        style={{
          fontFamily: 'var(--font-text)',
          fontSize: '10px',
          fontWeight: 700,
          letterSpacing: '0.14em',
          color: '#fff',
        }}
      >
        <span className="h-1 w-1 bg-white" aria-hidden />
        LIVE
      </span>
    </>
  )
}

function BicCardBody({ icon, title, desc }: { icon: ReactNode; title: string; desc: string }) {
  return (
    <div className="absolute inset-x-0 bottom-0 p-7">
      <div
        className="flex h-9 w-9 items-center justify-center text-white"
        style={{ border: '1px solid rgba(255,255,255,0.28)' }}
      >
        {icon}
      </div>
      <div
        className="mt-4 font-bold text-white"
        style={{ fontFamily: 'var(--font-display)', fontSize: '34px' }}
      >
        {title}
        <span className="text-brand-blue">.</span>
      </div>
      <p
        className="mt-1.5"
        style={{
          fontFamily: 'var(--font-text)',
          fontSize: '14px',
          color: 'rgba(255,255,255,0.75)',
        }}
      >
        {desc}
      </p>
    </div>
  )
}

function LabelsTab({
  labels,
  activeKey,
  onSelect,
  activeLabelName,
}: {
  labels: { key: string; name: string }[]
  activeKey: string | null
  onSelect: (key: string) => void
  activeLabelName: string
}) {
  void activeLabelName
  return (
    <div className="mt-s-7">
      {/* Label sub-tabs */}
      <div className="flex flex-wrap gap-s-2">
        {labels.map((l) => {
          const active = l.key === activeKey
          return (
            <button
              key={l.key}
              type="button"
              onClick={() => onSelect(l.key)}
              aria-current={active ? 'page' : undefined}
              style={{ fontFamily: 'var(--font-display)' }}
              className={[
                'border px-s-4 py-s-2 lf-body-sm font-medium transition-colors duration-base ease-standard focus-visible:outline-none focus-visible:shadow-focus',
                active
                  ? 'border-brand-blue bg-brand-blue text-fg-inverse'
                  : 'border-line-soft bg-surface-base text-fg-2 hover:border-line-strong hover:text-fg-1',
              ].join(' ')}
            >
              {l.name}
            </button>
          )
        })}
      </div>

      {/* Single "Creator Channel Dashboards" card per label → creator grid. */}
      {activeKey ? (
        <div className="mt-s-6 grid grid-cols-1 gap-s-4 sm:grid-cols-2 lg:grid-cols-3">
          <AppCard app={creatorDashboardsCard(activeKey)} index={0} gated={false} />
        </div>
      ) : null}
    </div>
  )
}

/* ============================================================
   Creator grid — /labels/:label/creators
   ============================================================ */
interface GridCreator {
  handle: string
  slug: string
  channelName: string
  profileImageUrl: string
  subscriberCount: number
  dashboardEnabled: boolean
  lastUploadAt?: string | null
  /** Commerce creator_social 에서 온 Instagram/TikTok 팔로워. 미매핑/무데이터면 null → 표시 생략. */
  instagramFollowers?: number | null
  tiktokFollowers?: number | null
}
type CreatorSort = 'subs' | 'name' | 'recent'
/** Compact subscriber count in "K" units (e.g. 1,440,000 → "1440K"). */
function formatSubsK(n: number): string {
  const v = Math.round(n || 0)
  if (v >= 1000) return `${Math.round(v / 1000).toLocaleString('en-US')}K`
  return String(v)
}
/* ── Creator card tuning (adjust here) ──────────────────────────────────
 *  CARD_NAME_FONT_PX : 채널명 글자 크기(px). 너무 긴 이름은 한 줄로 잘림(…).
 *  CARD_SUBS_FONT_PX : 구독자수 글자 크기(px). */
const CARD_NAME_FONT_PX = 24
const CARD_SUBS_FONT_PX = 16
const LABEL_NAME: Record<string, string> = Object.fromEntries(
  PORTAL_LABELS.map((l) => [l.key, l.name]),
)

// ---- Label Dashboard (aggregate masthead above the creator grid) ----
type LblPeriodKey = 'week' | 'month' | 'quarter' | 'year'
interface LblVideo {
  id: string
  prog: string
  creator: string
  ep: number | null
  form: 'Long' | 'Short'
  thumbnailUrl: string
  views: number
  conv: number
  rate: number
}
interface LblPeriod {
  range: string
  elapsed: string
  cmp: string
  totalViews: number
  watchHours: number
  netSubs: number
  uploads: { long: number; short: number; total: number }
  deltas: {
    views: number | null
    watch: number | null
    subs: number | null
    uploads: number | null
  }
  top: { views: LblVideo[]; conv: LblVideo[]; rate: LblVideo[] }
}
interface LblCoverage {
  creators: number
  dailyDays: number
  dailyFirst: string | null
  dailyLast: string | null
  videos: number
  programVideos: number
}
interface LblDash {
  label: string
  count: number
  today: string
  asOf?: string
  coverage?: LblCoverage
  periods: Record<LblPeriodKey, LblPeriod>
}

const LBL_PERIODS: { key: LblPeriodKey; ko: string }[] = [
  { key: 'week', ko: '주' },
  { key: 'month', ko: '월' },
  { key: 'quarter', ko: '분기' },
  { key: 'year', ko: '연' },
]

// Korean 만/억 formatter — mirrors the handoff fmt().
function fmtMan(n: number): string {
  n = Math.round(n)
  const neg = n < 0
  const a = Math.abs(n)
  let s: string
  if (a >= 1e8) s = (a / 1e8).toFixed(a >= 1e9 ? 0 : 1).replace(/\.0$/, '') + '억'
  else if (a >= 1e4) s = (a / 1e4).toFixed(a >= 1e6 ? 0 : 1).replace(/\.0$/, '') + '만'
  else if (a >= 1e3) s = a.toLocaleString('en-US')
  else s = String(a)
  return (neg ? '−' : '') + s
}

function DeltaRow({ d, cmp }: { d: number | null; cmp: string }) {
  if (d == null) {
    return (
      <div className="lblm-pd">
        <b className="flat">—</b>
        <span>비교 기준 없음</span>
      </div>
    )
  }
  const up = d >= 0
  return (
    <div className="lblm-pd">
      <b className={up ? 'up' : 'down'}>
        <span className="arw">{up ? '▲' : '▼'}</span>
        {Math.abs(d).toFixed(1)}%
      </b>
      <span>{cmp}</span>
    </div>
  )
}

function LblBoard({
  title,
  by,
  metric,
  rows,
}: {
  title: string
  by: string
  metric: 'views' | 'conv' | 'rate'
  rows: LblVideo[]
}) {
  return (
    <div className="lblm-board">
      <h4>
        {title}
        <span className="by">By {by}</span>
      </h4>
      {rows.length === 0 ? (
        <p className="lblm-board-empty">이 기간 업로드된 허브 프로그램 영상이 없어요.</p>
      ) : (
        rows.map((v, i) => {
          const val =
            metric === 'rate' ? (
              <>
                {v.rate.toFixed(1)}
                <span className="x">%</span>
              </>
            ) : metric === 'conv' ? (
              '+' + fmtMan(v.conv)
            ) : (
              fmtMan(v.views)
            )
          return (
            <div className="lblm-vrow" key={v.id + metric}>
              <div className="rno">{i + 1}</div>
              <div className="lblm-vth">
                {v.thumbnailUrl ? (
                  <img src={v.thumbnailUrl} alt="" loading="lazy" referrerPolicy="no-referrer" />
                ) : (
                  <span className="ini">{v.prog.charAt(0)}</span>
                )}
                <span className="frm">{v.form}</span>
              </div>
              <div className="lblm-vmeta">
                <div className="pg" title={v.prog}>
                  {v.prog}
                </div>
                <div className="cr">
                  {v.ep != null ? <span className="ep">EP.{v.ep}</span> : null}
                  {v.ep != null ? ' · ' : ''}
                  {v.creator}
                </div>
              </div>
              <div className="lblm-vval tnum">{val}</div>
            </div>
          )
        })
      )}
    </div>
  )
}

function LabelDashboard({ label }: { label: string }) {
  const [data, setData] = useState<LblDash | null>(null)
  const [period, setPeriod] = useState<LblPeriodKey>('week')
  const [errored, setErrored] = useState(false)

  useEffect(() => {
    let alive = true
    fetch(`/api/labels/${encodeURIComponent(label)}/dashboard`, { credentials: 'same-origin' })
      .then(async (r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`)
        return (await r.json()) as LblDash
      })
      .then((d) => {
        if (alive) setData(d)
      })
      .catch(() => {
        if (alive) setErrored(true)
      })
    return () => {
      alive = false
    }
  }, [label])

  // On error / not-yet-loaded, render nothing — the grid below owns messaging.
  if (errored || !data) return null
  const p = data.periods[period]
  if (!p) return null

  return (
    <section className="lblm">
      <div className="lblm-mast">
        <div className="lblm-bar">
          <div>
            <span className="t">Label Dashboard</span>
            <span className="c">
              {p.range} · {p.elapsed}
            </span>
          </div>
          <div className="lblm-seg" role="tablist" aria-label="기간">
            {LBL_PERIODS.map((it) => (
              <button
                key={it.key}
                type="button"
                role="tab"
                aria-selected={it.key === period}
                className={it.key === period ? 'on' : ''}
                onClick={() => setPeriod(it.key)}
              >
                {it.ko}
              </button>
            ))}
          </div>
        </div>

        <div className="lblm-cmp">
          <b>동기간 대비(WTD)</b> — {p.cmp} 대비 성장률. 데이터 기준일{' '}
          <b>{data.asOf || data.today}</b>
          {data.asOf && data.asOf !== data.today.slice(0, 10).replace(/\./g, '-')
            ? ' (YouTube 집계가 ~2–3일 지연돼, 가장 최근 집계일까지 누적)'
            : ''}
          , 비교 구간도 같은 경과 시점까지만 집계됩니다.
          {data.coverage && data.coverage.dailyDays === 0 ? (
            <span style={{ color: 'var(--status-danger)' }}>
              {' '}
              · 일별 집계 데이터가 없어요 — 채널 OAuth 수집이 필요합니다.
            </span>
          ) : null}
        </div>

        <div className="lblm-perf">
          <div className="lblm-cell">
            <div className="lblm-pk">Total views</div>
            <div className="lblm-pv">{fmtMan(p.totalViews)}</div>
            <DeltaRow d={p.deltas.views} cmp={p.cmp} />
            <div className="lblm-psub">레이블 {data.count}개 채널 합산</div>
          </div>
          <div className="lblm-cell">
            <div className="lblm-pk">Watch time</div>
            <div className="lblm-pv">
              {fmtMan(p.watchHours)}
              <span className="u">hrs</span>
            </div>
            <DeltaRow d={p.deltas.watch} cmp={p.cmp} />
            <div className="lblm-psub">조회수 × 평균 시청시간</div>
          </div>
          <div className={'lblm-cell accent' + (p.netSubs > 0 ? ' pos' : '')}>
            <div className="lblm-pk">Net subscribers</div>
            <div className="lblm-pv">{(p.netSubs >= 0 ? '+' : '') + fmtMan(p.netSubs)}</div>
            <DeltaRow d={p.deltas.subs} cmp={p.cmp} />
            <div className="lblm-psub">구독 순증감 (신규 − 취소)</div>
          </div>
          <div className="lblm-cell">
            <div className="lblm-pk">New uploads</div>
            <div className="lblm-pv">{p.uploads.total}</div>
            <DeltaRow d={p.deltas.uploads} cmp={p.cmp} />
            <div className="lblm-psub">
              Long <b>{p.uploads.long}</b> · Short <b>{p.uploads.short}</b>
            </div>
          </div>
        </div>
      </div>

      <div className="lblm-boards-head">
        <span className="t">Hub Programs · Top performers</span>
        <span className="s">이 기간 업로드된 허브 프로그램 영상 · 항목별 상위 5</span>
      </div>
      <div className="lblm-boards">
        <LblBoard title="Most viewed" by="조회수" metric="views" rows={p.top.views} />
        <LblBoard title="Sub conversion" by="구독전환" metric="conv" rows={p.top.conv} />
        <LblBoard title="Watch rate" by="시청률" metric="rate" rows={p.top.rate} />
      </div>
    </section>
  )
}

function CreatorGridPage() {
  const { label = '' } = useParams()
  const navigate = useNavigate()
  const [state, setState] = useState<'loading' | 'ok' | 'error' | 'forbidden'>('loading')
  const [creators, setCreators] = useState<GridCreator[]>([])
  const [query, setQuery] = useState('')
  const [sort, setSort] = useState<CreatorSort>('subs') // 기본: 구독자 내림차순
  const labelName = LABEL_NAME[label] || label
  const q = query.trim().toLowerCase()
  const filtered = q
    ? creators.filter(
        (c) => c.channelName.toLowerCase().includes(q) || c.handle.toLowerCase().includes(q),
      )
    : creators
  const sorted = [...filtered].sort((a, b) => {
    if (sort === 'name') return a.channelName.localeCompare(b.channelName, 'ko')
    if (sort === 'recent') {
      const ta = a.lastUploadAt ? Date.parse(a.lastUploadAt) : 0
      const tb = b.lastUploadAt ? Date.parse(b.lastUploadAt) : 0
      return tb - ta
    }
    return (b.subscriberCount || 0) - (a.subscriberCount || 0)
  })

  const load = useCallback(() => {
    setState('loading')
    fetch(`/api/labels/${encodeURIComponent(label)}/creators`, { credentials: 'same-origin' })
      .then(async (r) => {
        if (r.status === 403) {
          setState('forbidden')
          return
        }
        if (!r.ok) throw new Error(`HTTP ${r.status}`)
        const d = (await r.json()) as { creators?: GridCreator[] }
        setCreators(Array.isArray(d.creators) ? d.creators : [])
        setState('ok')
      })
      .catch(() => setState('error'))
  }, [label])
  useEffect(() => {
    load()
  }, [load])

  const gridCls = 'mt-s-7 grid grid-cols-2 gap-s-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5'
  const emptyBox =
    'mt-s-7 flex min-h-[260px] flex-col items-center justify-center border border-dashed border-line-soft bg-surface-soft p-s-9 text-center'

  return (
    <div className="pt-s-7">
      <div className="flex items-center justify-between gap-s-4 flex-wrap">
        <div>
          <p className="lf-eyebrow text-fg-3">▪ Labels &amp; Creator Solutions</p>
          <h1 className="mt-s-2 lf-h3 text-fg-1" style={{ fontFamily: 'var(--font-display)' }}>
            {labelName} · Creator Channel Dashboards
          </h1>
        </div>
        <button
          type="button"
          onClick={() => {
            // 항상 레이블 탭(이 그리드의 상위 화면)으로. 브라우저 히스토리에
            // 의존하지 않아 어디서 진입했든 동일하게 동작한다.
            navigate(`/?tab=labels&label=${encodeURIComponent(label)}`)
          }}
          className="border border-line-soft bg-surface-base px-s-4 py-s-2 lf-body-sm text-fg-2 hover:border-line-strong hover:text-fg-1"
        >
          ← 레이블 목록
        </button>
      </div>

      {/* Aggregate label dashboard masthead (above the creator grid). */}
      {state !== 'forbidden' ? <LabelDashboard label={label} /> : null}

      {/* Channels section header — separates the grid from the masthead. */}
      {state === 'ok' && creators.length > 0 ? (
        <div className="mt-s-12 flex items-baseline gap-s-3 border-b border-fg-1 pb-s-3">
          <span className="lf-eyebrow text-fg-1" style={{ letterSpacing: '0.16em' }}>
            Channels
          </span>
          <span className="lf-body-sm text-fg-3 tabular-nums">{creators.length}명</span>
        </div>
      ) : null}

      {state === 'loading' ? (
        <div className={gridCls}>
          {[0, 1, 2, 3, 4].map((i) => (
            <div
              key={i}
              className="border border-line-soft bg-surface-soft animate-pulse"
              style={{ aspectRatio: '3 / 4' }}
            />
          ))}
        </div>
      ) : state === 'error' ? (
        <div className={emptyBox}>
          <p className="lf-h3 text-fg-1">불러오지 못했어요.</p>
          <button
            type="button"
            onClick={load}
            className="mt-s-4 border border-brand-blue bg-brand-blue px-s-5 py-s-2 lf-body-sm text-fg-inverse"
          >
            재시도
          </button>
        </div>
      ) : state === 'forbidden' ? (
        <div className={emptyBox}>
          <p className="lf-eyebrow text-status-danger">▪ No access</p>
          <p className="mt-s-3 lf-h3 text-fg-1">이 레이블에 접근 권한이 없어요.</p>
        </div>
      ) : creators.length === 0 ? (
        <div className={emptyBox}>
          <p className="lf-eyebrow text-fg-3">▪ {labelName}</p>
          <p className="mt-s-3 lf-h3 text-fg-1">아직 등록된 크리에이터가 없어요.</p>
          <p className="mt-s-2 lf-body-sm text-fg-3 max-w-[42ch]">
            관리자 화면에서 YouTube 채널 URL로 크리에이터를 추가할 수 있어요.
          </p>
        </div>
      ) : (
        <>
          {/* Search + sort toolbar. */}
          <div className="mt-s-6 flex items-center justify-between gap-s-3 flex-wrap">
            <div className="flex items-center gap-s-3 flex-wrap">
              <div className="relative w-full max-w-[320px]">
                <input
                  type="search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="채널명으로 검색"
                  aria-label="채널명으로 검색"
                  className="w-full border border-line-soft bg-surface-base py-s-2 pl-s-8 pr-s-3 lf-body-sm text-fg-1 placeholder:text-fg-3 focus-visible:outline-none focus-visible:border-brand-blue focus-visible:shadow-focus"
                />
                <span
                  className="pointer-events-none absolute left-s-3 top-1/2 -translate-y-1/2 text-fg-3"
                  aria-hidden="true"
                >
                  ⌕
                </span>
              </div>
              <span className="lf-body-sm text-fg-3 tabular-nums">{filtered.length}명</span>
            </div>
            <div className="inline-flex border border-line-soft" role="group" aria-label="정렬">
              {(
                [
                  ['subs', '구독자순'],
                  ['name', '가나다'],
                  ['recent', '최근 업로드'],
                ] as [CreatorSort, string][]
              ).map(([key, lbl], i) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setSort(key)}
                  aria-pressed={sort === key}
                  className={[
                    'px-s-3 py-s-2 lf-body-sm font-medium transition-colors duration-base ease-standard',
                    i > 0 ? 'border-l border-line-soft' : '',
                    sort === key
                      ? 'bg-brand-blue text-fg-inverse'
                      : 'bg-surface-base text-fg-2 hover:text-fg-1',
                  ].join(' ')}
                >
                  {lbl}
                </button>
              ))}
            </div>
          </div>

          {filtered.length === 0 ? (
            <div className={emptyBox}>
              <p className="lf-eyebrow text-fg-3">▪ {labelName}</p>
              <p className="mt-s-3 lf-h3 text-fg-1">검색 결과가 없어요.</p>
              <p className="mt-s-2 lf-body-sm text-fg-3">“{query}”와 일치하는 채널명이 없습니다.</p>
            </div>
          ) : (
            <div className={gridCls}>
              {sorted.map((c) => (
                <CreatorCell key={c.handle} c={c} />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  )
}

function CreatorCell({ c }: { c: GridCreator }) {
  const inner = (
    <div className="absolute inset-0 overflow-hidden bg-grad-blue-brown">
      {/* full-bleed profile photo */}
      {c.profileImageUrl ? (
        <img
          src={c.profileImageUrl}
          alt=""
          referrerPolicy="no-referrer"
          loading="lazy"
          className="absolute inset-0 h-full w-full object-cover transition-transform duration-slow ease-out-soft group-hover:scale-[1.04]"
        />
      ) : null}
      {/* legibility scrim — top transparent → ink at the bottom */}
      <div
        className="absolute inset-0"
        style={{
          background:
            'linear-gradient(180deg, rgba(26,18,19,0) 36%, rgba(26,18,19,0.5) 64%, rgba(26,18,19,0.9) 100%)',
        }}
      />
      {/* top eyebrow mark */}
      <div className="absolute left-s-4 top-s-4 flex items-center gap-s-2">
        <span className="h-[7px] w-[7px]" style={{ background: 'var(--lcmd-pink, #FC8FFF)' }} />
        <span
          className="text-[10px] font-semibold uppercase tracking-[0.18em] text-white/80"
          style={{ fontFamily: 'var(--font-text)' }}
        >
          Creator
        </span>
      </div>
      {/* bottom overlay — channel name + subscribers */}
      <div className="absolute inset-x-0 bottom-0 p-s-4">
        <p
          className="truncate font-bold text-white"
          style={{
            fontFamily: 'var(--font-display)',
            fontSize: `${CARD_NAME_FONT_PX}px`,
            lineHeight: 1.1,
            letterSpacing: '-0.015em',
          }}
        >
          {c.channelName}
        </p>
        <p
          className="mt-[6px] tabular-nums text-white/95"
          style={{ fontSize: `${CARD_SUBS_FONT_PX}px` }}
        >
          <span className="font-light" style={{ fontFamily: 'var(--font-text)' }}>
            {formatSubsK(c.subscriberCount)}
          </span>
          <span
            className="ml-[5px] text-white/50 font-light"
            style={{ fontSize: '11px', letterSpacing: '0.04em' }}
          >
            Subscribers
          </span>
        </p>
        {(c.instagramFollowers != null || c.tiktokFollowers != null) && (
          <p
            className="mt-[3px] tabular-nums text-white/60 font-light"
            style={{ fontFamily: 'var(--font-text)', fontSize: '11px', letterSpacing: '0.02em' }}
          >
            {c.instagramFollowers != null && <span>IG {formatSubsK(c.instagramFollowers)}</span>}
            {c.instagramFollowers != null && c.tiktokFollowers != null && (
              <span className="mx-[5px] text-white/30">·</span>
            )}
            {c.tiktokFollowers != null && <span>TikTok {formatSubsK(c.tiktokFollowers)}</span>}
          </p>
        )}
      </div>
      {!c.dashboardEnabled ? (
        <span className="absolute right-s-3 top-s-3 inline-block bg-black/55 px-s-2 py-[2px] text-[10px] font-semibold tracking-[0.1em] text-white">
          준비 중
        </span>
      ) : null}
    </div>
  )
  const frame = 'group relative block w-full overflow-hidden border border-line-soft'
  if (!c.dashboardEnabled) {
    return (
      <div
        className={`${frame} cursor-not-allowed opacity-60`}
        style={{ aspectRatio: '3 / 4' }}
        aria-disabled="true"
      >
        {inner}
      </div>
    )
  }
  return (
    <a
      href={`/dashboard/${encodeURIComponent(c.slug)}/overview`}
      className={`${frame} transition-colors duration-base ease-standard hover:border-line-strong focus-visible:outline-none focus-visible:shadow-focus`}
      style={{ aspectRatio: '3 / 4' }}
      title={c.channelName}
    >
      {inner}
    </a>
  )
}

/**
 * Featured banner — LCMD SNS look-and-feel pattern (preview 28).
 *
 * Per-app cover imagery is the *visual identity* for that app — the
 * thing that makes Artifacts look like Artifacts at a glance, not like
 * the next app we'll ship. Putting the same brand gradient + ▪ mark on
 * every card would erase that signal, so the cover stays full-bleed
 * and the chrome (eyebrow / LIVE chip / headline / footer) sits in a
 * fixed four-corner grid on top of it.
 *
 * Text layout mirrors preview 28 SNS posts: small metadata in the top
 * corners (category eyebrow TL · LIVE square chip TR), big display
 * headline anchored bottom-left, hostname + Open affordance bottom-
 * right. The LIVE chip uses the square modifier vocabulary (preview 30),
 * not a pill — same register as the "3" badge in preview 28 grid #1.
 *
 * Scrim is two-layer: a soft top wash and a stronger bottom wash, so
 * the cover stays visible through the middle while the corner copy
 * always reads as ink-on-cover. Pure black gradient (not Ink-800) is
 * used in the scrims specifically — they have to read as shadow, not
 * as brand color sitting on top of the imagery.
 */
function FeaturedCard({ app, index }: { app: AppEntry; index: number }) {
  const isActive = app.status === 'active'
  const ordinal = String(index + 1).padStart(2, '0')
  const hostname = app.url ? hostnameOf(app.url) : null

  const baseClasses =
    'group relative block w-full overflow-hidden bg-brand-brown text-fg-inverse transition-shadow duration-base ease-standard aspect-[4/3] sm:aspect-[16/9] lg:aspect-[3/1]'

  const inner = (
    <>
      {/* Cover — full bleed. This IS the app's icon. */}
      {app.cover ? (
        <img
          src={app.cover}
          alt=""
          className="absolute inset-0 h-full w-full object-cover transition-transform duration-slow ease-out-soft group-hover:scale-[1.02]"
        />
      ) : (
        <div className="absolute inset-0 bg-grad-pink-grey" />
      )}

      {/* Scrims — top soft, bottom stronger, mid stays clear */}
      <div
        aria-hidden
        className="absolute inset-x-0 top-0 h-1/3"
        style={{
          background: 'linear-gradient(to bottom, rgba(0,0,0,0.42) 0%, rgba(0,0,0,0.0) 100%)',
        }}
      />
      <div
        aria-hidden
        className="absolute inset-x-0 bottom-0 h-3/4"
        style={{
          background:
            'linear-gradient(to top, rgba(0,0,0,0.78) 0%, rgba(0,0,0,0.35) 45%, rgba(0,0,0,0.0) 100%)',
        }}
      />

      {/* TL — category eyebrow */}
      <p className="absolute left-s-6 top-s-6 lg:left-s-9 lg:top-s-7 lf-eyebrow inline-flex items-center gap-s-2 text-white/85">
        <span aria-hidden>▪</span>
        <span>Apps</span>
        <span aria-hidden className="inline-block h-px w-3 bg-white/45" />
        <span className="lf-num">{ordinal}</span>
      </p>

      {/* TR — LIVE square modifier chip (preview 28 / preview 30) */}
      <div className="absolute right-s-6 top-s-6 lg:right-s-9 lg:top-s-7">
        {isActive ? (
          <span className="lf-eyebrow inline-flex items-center gap-s-2 bg-brand-pink px-s-3 py-1 text-brand-brown">
            <span aria-hidden>▪</span>
            <span>Live</span>
          </span>
        ) : (
          <span className="lf-eyebrow inline-flex items-center gap-s-2 border border-white/30 bg-black/30 px-s-3 py-1 text-white/85 backdrop-blur-sm">
            <span aria-hidden>▪</span>
            <span>Coming soon</span>
          </span>
        )}
      </div>

      {/* Bottom row — BL headline + body · BR hostname + Open */}
      <div className="absolute inset-x-0 bottom-0 flex items-end justify-between gap-s-6 p-s-6 lg:p-s-9">
        <div className="min-w-0 max-w-[68%]">
          <h2
            className="font-display font-bold leading-[0.95] tracking-[-0.015em] text-fg-inverse"
            style={{ fontSize: 'clamp(40px, 6.5vw, 92px)' }}
          >
            {app.name}
            <span className="text-brand-pink">.</span>
          </h2>
          {/* text-balance keeps the wrap even — no orphan "text." line
              when the description nearly fits. Parent already caps width
              at 68% of the card, so no extra max-w is needed here. */}
          <p className="mt-s-3 lf-body-sm text-balance text-white/85">{app.description}</p>
        </div>

        <div className="hidden flex-shrink-0 flex-col items-end gap-s-2 lg:flex">
          {hostname ? <p className="lf-eyebrow lf-num text-white/65">{hostname}</p> : null}
          {isActive && (app.url || app.to) ? (
            <p className="lf-eyebrow inline-flex items-center gap-s-2 text-brand-pink transition-transform duration-base ease-standard group-hover:translate-x-[2px]">
              <span>Open</span>
              <span aria-hidden>→</span>
            </p>
          ) : null}
        </div>
      </div>
    </>
  )

  // Internal SPA route — react-router push, no cross-origin navigation.
  if (isActive && app.to) {
    return (
      <Link to={app.to} className={`${baseClasses} hover:shadow-lf-pop`}>
        {inner}
      </Link>
    )
  }
  if (isActive && app.url) {
    return (
      <a href={app.url} className={`${baseClasses} hover:shadow-lf-pop`}>
        {inner}
      </a>
    )
  }

  return (
    <div role="group" aria-disabled={!isActive} className={`${baseClasses} cursor-default`}>
      {inner}
    </div>
  )
}

function hostnameOf(url: string): string {
  try {
    return new URL(url).hostname
  } catch {
    return url
  }
}

function AppCard({ app, index, gated }: { app: AppEntry; index: number; gated: boolean }) {
  const Icon = app.icon
  // A gated app behaves like coming-soon for this user: card is shown,
  // labelled "Beta", and isn't a link. Server-side gate is the real
  // wall; this just avoids landing them on a page that immediately
  // 403s.
  const isActive = app.status === 'active' && !gated
  const ordinal = String(index + 1).padStart(2, '0')

  // Cover variant — same editorial language as the featured banner but at
  // the smaller AppCard size. Cover image full-bleed, scrim, top eyebrow
  // + Live badge, bottom title + body. Falls back to the original
  // border-only card when no cover image is supplied.
  if (app.cover || app.coverFallback) {
    const innerCover = (
      <>
        {app.cover ? (
          <img
            src={app.cover}
            alt=""
            className="absolute inset-0 h-full w-full object-cover transition-transform duration-slow ease-out-soft group-hover:scale-[1.03]"
          />
        ) : (
          <div
            aria-hidden
            className={`absolute inset-0 transition-transform duration-slow ease-out-soft group-hover:scale-[1.03] ${app.coverFallback ?? ''}`}
          />
        )}
        {/* Top + bottom scrims for text legibility */}
        <div
          aria-hidden
          className="absolute inset-x-0 top-0 h-1/3"
          style={{
            background: 'linear-gradient(to bottom, rgba(0,0,0,0.42) 0%, rgba(0,0,0,0.0) 100%)',
          }}
        />
        <div
          aria-hidden
          className="absolute inset-x-0 bottom-0 h-2/3"
          style={{
            background:
              'linear-gradient(to top, rgba(0,0,0,0.82) 0%, rgba(0,0,0,0.4) 45%, rgba(0,0,0,0.0) 100%)',
          }}
        />

        {/* TL — Apps · NN eyebrow */}
        <p className="absolute left-s-5 top-s-5 lf-eyebrow inline-flex items-center gap-s-2 text-white/85">
          <span aria-hidden>▪</span>
          <span>Apps</span>
          <span aria-hidden className="inline-block h-px w-3 bg-white/45" />
          <span className="lf-num">{ordinal}</span>
        </p>

        {/* TR — Live / Coming soon */}
        <div className="absolute right-s-5 top-s-5">
          {isActive ? (
            <span className="lf-eyebrow inline-flex items-center gap-s-2 bg-brand-pink px-s-2 py-1 text-brand-brown">
              <span aria-hidden>▪</span>
              <span>Live</span>
            </span>
          ) : (
            <span className="lf-eyebrow inline-flex items-center gap-s-2 border border-white/30 bg-black/30 px-s-2 py-1 text-white/85 backdrop-blur-sm">
              <span aria-hidden>▪</span>
              <span>Coming soon</span>
            </span>
          )}
        </div>

        {/* Bottom — small icon + title + body. Title uses Grato Classic
            display at a card-scaled size (Artifacts featured headline is
            much bigger, this stays under it but reads as a real hero). */}
        <div className="absolute inset-x-0 bottom-0 p-s-5 lg:p-s-6">
          <span className="inline-flex h-7 w-7 items-center justify-center bg-white/15 text-fg-inverse ring-1 ring-white/25 backdrop-blur-sm">
            <Icon size={14} strokeWidth={1.5} />
          </span>
          <h3
            className="mt-s-3 font-display font-bold leading-[0.95] tracking-[-0.01em] text-fg-inverse"
            style={{ fontSize: 'clamp(22px, 2.8vw, 30px)' }}
          >
            {app.name}
            <span className="text-brand-pink">.</span>
          </h3>
          <p className="mt-s-2 line-clamp-2 lf-body-sm text-white/85">{app.description}</p>
        </div>
      </>
    )

    const coverClasses =
      'group relative block w-full overflow-hidden bg-brand-brown text-fg-inverse transition-shadow duration-base ease-standard aspect-[4/3]'

    if (isActive && app.to) {
      return (
        <Link to={app.to} className={`${coverClasses} hover:shadow-lf-pop`}>
          {innerCover}
        </Link>
      )
    }
    if (isActive && app.url) {
      return (
        <a href={app.url} className={`${coverClasses} hover:shadow-lf-pop`}>
          {innerCover}
        </a>
      )
    }
    return (
      <div className={`${coverClasses} cursor-default`} aria-disabled={!isActive}>
        {innerCover}
      </div>
    )
  }

  // Border variant — original card for apps without a cover image.
  const baseClasses =
    'group relative flex h-full flex-col gap-s-4 p-s-6 transition-all duration-base ease-standard bg-surface-base border border-line-hairline hover:border-line-soft hover:shadow-lf-1'

  const content = (
    <>
      <div className="flex items-center justify-between">
        <span className="inline-flex h-9 w-9 items-center justify-center bg-surface-soft text-fg-1">
          <Icon size={18} strokeWidth={1.5} />
        </span>
        {isActive ? (
          <span className="lf-eyebrow text-brand-pink">▪ Live</span>
        ) : (
          <span className="lf-eyebrow">▪ Coming soon</span>
        )}
      </div>

      <div>
        <h3 className="lf-h3">{app.name}</h3>
        <p className="mt-s-2 lf-body-sm text-fg-2">{app.description}</p>
      </div>
    </>
  )

  if (isActive && app.to) {
    return (
      <Link to={app.to} className={baseClasses}>
        {content}
      </Link>
    )
  }
  if (isActive && app.url) {
    return (
      <a href={app.url} className={baseClasses}>
        {content}
      </a>
    )
  }

  return (
    <div role="group" aria-disabled={!isActive} className={`${baseClasses} cursor-default`}>
      {content}
    </div>
  )
}
